import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CountryCombobox } from "@/components/ui/CountryCombobox";
import { Select } from "@/components/ui/design-system";
import { countryLabel } from "@/lib/employeeRecordLabels";
import { foreignFields, isCountryDocumentField, type DocumentPreview } from "@/lib/permitDocuments";

export type PreviewField = DocumentPreview["fields"][number];
// Only fields accepted by the existing partial worker update are editable here.
// Employment termination needs a reason and stays in the personnel card.
const workerInputs: Record<string, { type: string; maxLength?: number }> = {
  ime: { type: "text", maxLength: 80 }, prezime: { type: "text", maxLength: 80 },
  oib: { type: "text", maxLength: 11 }, broj_putovnice: { type: "text", maxLength: 120 },
  datum_rodenja: { type: "date" }, drzavljanstvo_iso2: { type: "country" },
  spol: { type: "gender" }, adresa_boraviste: { type: "text", maxLength: 1000 },
  datum_pocetka_rada: { type: "date" }, datum_prijave_osiguranja: { type: "date" },
};

export function documentFieldValue(field: PreviewField) {
  if (!field.value) return "Nedostaje podatak";
  return isCountryDocumentField(field) ? countryLabel(field.value) : field.value;
}

export function PermitDocumentField({ field, workerId, fixed, onSave, onDirty }: {
  field: PreviewField; workerId?: string; fixed: boolean;
  onSave: (value: string) => Promise<void>; onDirty: () => void;
}) {
  const { can } = useAuth();
  const key = field.source.startsWith("radnik.") ? field.source.slice(7) : "";
  const foreign = foreignFields.find(([name]) => name === key);
  const input = workerInputs[key] ?? (foreign ? { type: foreign[2], maxLength: key === "foreign_address" ? 1000 : ["marital_status", "visa_type", "visa_number"].includes(key) ? 100 : 200 } : undefined);
  const editable = field.manual ? !fixed : field.missing && !!workerId && !!input && can("radnik", "update") && (!foreign || can("dozvola", "update"));
  const [value, setValue] = useState(field.manual ? field.value ?? "" : "");
  const update = (next: string) => { setValue(next); onDirty(); };
  const country = isCountryDocumentField(field);
  return <div className="space-y-2 rounded border p-3">
    <p className="font-semibold">{field.label}</p>
    {field.warning && <p role="status">{field.warning}</p>}
    {editable ? <>
      {country ? <CountryCombobox label={field.label} value={value} valueFormat={key === "drzavljanstvo_iso2" ? "code" : "name"} onChange={update} />
        : input?.type === "gender" ? <label className="block">{field.label}<Select value={value} onChange={event => update(event.target.value)}><option value="">Odaberite spol</option><option value="muski">Muški</option><option value="zenski">Ženski</option></Select></label>
          : field.manual ? <label className="block">{field.label}<textarea className="min-h-11 w-full rounded border p-2" maxLength={4000} value={value} onChange={event => update(event.target.value)} /></label>
            : <label className="block">{field.label}<Input type={input?.type} maxLength={input?.maxLength} value={value} onChange={event => update(event.target.value)} /></label>}
      <Button type="button" disabled={!value.trim() && (!field.manual || field.required)} onClick={() => void onSave(value.trim())}>{field.manual ? `Primijeni: ${field.label}` : `Spremi u karton: ${field.label}`}</Button>
    </> : <>
      <p className={field.missing ? "font-semibold text-[var(--color-status-bad-fg)]" : "whitespace-pre-wrap"}>{documentFieldValue(field)}</p>
      {field.missing && field.card_url && <Link className="inline-flex min-h-11 items-center underline" to={field.card_url}>Dopuni u kartonu</Link>}
    </>}
  </div>;
}
